import React, { useEffect, useRef, useState } from "react";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import QrScanner from "qr-scanner";

interface QrScannerModalProps {
  show: boolean;
  onScan: (text: string) => void;
  onClose: () => void;
}

const QrScannerModal: React.FC<QrScannerModalProps> = ({
  show,
  onScan,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"camera" | "file">("camera");
  const [error, setError] = useState<string | null>(null);
  const [fileScanning, setFileScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Start/stop camera scanner when tab or visibility changes
  useEffect(() => {
    if (!show || activeTab !== "camera") {
      scannerRef.current?.stop();
      return;
    }

    if (!videoRef.current) return;

    const scanner = new QrScanner(
      videoRef.current,
      (result) => {
        scanner.stop();
        onScan(result.data);
      },
      {
        preferredCamera: "environment",
        highlightScanRegion: true,
        highlightCodeOutline: true,
        onDecodeError: () => {
          // suppress per-frame decode errors
        },
      },
    );

    scanner.start().catch(() => {
      setError(
        "No se pudo acceder a la cámara. Verificá los permisos o usá la opción de archivo.",
      );
    });

    scannerRef.current = scanner;

    return () => {
      scanner.stop();
      scanner.destroy();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, activeTab]);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setError(null);
      setActiveTab("camera");
    }
  }, [show]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setFileScanning(true);
    try {
      const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
      onScan(result.data);
    } catch {
      setError(
        "No se encontró un código QR válido en la imagen. Intentá con otra imagen o ingresá el CDC manualmente.",
      );
    } finally {
      setFileScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <CModal show={show} onClose={onClose} size="lg" centered>
      <CModalHeader>
        <CModalTitle>Escanear código QR</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CNav variant="tabs" className="mb-3">
          <CNavItem>
            <CNavLink
              active={activeTab === "camera"}
              onClick={() => setActiveTab("camera")}
              style={{ cursor: "pointer" }}
            >
              Cámara
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === "file"}
              onClick={() => setActiveTab("file")}
              style={{ cursor: "pointer" }}
            >
              Subir imagen
            </CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent>
          <CTabPane active={activeTab === "camera"}>
            <p className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
              Apuntá la cámara al código QR del KUDE.
            </p>
            <video
              ref={videoRef}
              style={{ width: "100%", borderRadius: 4 }}
            />
          </CTabPane>

          <CTabPane active={activeTab === "file"}>
            <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
              Seleccioná una imagen del comprobante que contenga el código QR.
            </p>
            <div className="d-flex align-items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={fileScanning}
                style={{ flex: 1 }}
              />
              {fileScanning && (
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Procesando...
                </span>
              )}
            </div>
          </CTabPane>
        </CTabContent>

        {error && (
          <div className="alert alert-warning mt-3 mb-0" role="alert">
            {error}
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Cancelar
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default QrScannerModal;
